import { productAxiosInstance } from "@/services/axios";

export interface NavigationItem {
  id: string | number;
  title: string;
  heading?: string;
  url: string;
  slug?: string;
  is_navbar_label?: boolean;
  isNew?: boolean;
  product_count?: number;
  subTypes?: NavigationItem[];
  children?: NavigationItem[];
  content?: string;
  applicable_vouchers?: unknown[];
}

interface RawNavigationItem {
  id: string | number;
  name?: string;
  label?: string;
  heading?: string;
  title?: string;
  url?: string;
  is_new?: boolean;
  product_count?: number;
  children?: RawNavigationItem[];
  content?: string;
  slug?: string;
  applicable_vouchers?: unknown[];
}

interface RawNavBarLabel {
  id: string | number;
  name: string;
  slug?: string;
  content?: string;
  collections?: RawNavigationItem[];
}

const getNavigationLinks = async (
  params: Record<string, string | number | boolean | undefined> = {},
  platformCode: string = "TP",
) => {
  try {
    const response = await productAxiosInstance.get("/navbar/", {
      params,
      headers: {
        "x-platform-code": platformCode,
      },
    });

    const { nav_bar_labels = [], nav_bar_collections = [] } = response.data;

    const scrubUrl = (url: string) =>
      url?.replace(/parent_nav_collection_id=/g, "category_id=");

    const slugify = (text: string) =>
      text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");

    // Helper to map nested items recursively
    const mapItems = (
      items: RawNavigationItem[],
      umbrellaSlug: string,
    ): NavigationItem[] => {
      return (items || []).map((item: RawNavigationItem): NavigationItem => {
        const titleStr = (item.name ||
          item.label ||
          item.heading ||
          item.title) as string;

        const ownSlug =
          item.slug?.toLowerCase() ||
          (titleStr ? slugify(titleStr) : umbrellaSlug);

        const hasChildren = item.children && item.children.length > 0;
        const subTypes = hasChildren
          ? mapItems(item.children as RawNavigationItem[], ownSlug)
          : [];

        return {
          id: item.id,
          title: titleStr,
          heading: titleStr,
          slug: ownSlug,
          // Umbrella pattern: /listing/<own-slug>?id=<id>
          url: scrubUrl(item.url || `/listing/${ownSlug}?id=${item.id}`),
          isNew: item.is_new || false,
          product_count: item.product_count ?? 0,
          subTypes: subTypes,
          children: subTypes,
          content: item.content || "",
          applicable_vouchers: item.applicable_vouchers || [],
        };
      });
    };

    const navigation: NavigationItem[] = [];

    // 1. Process nav_bar_labels (Grouped labels with collections)
    if (nav_bar_labels && nav_bar_labels.length > 0) {
      const labels = nav_bar_labels.map(
        (label: RawNavBarLabel, index: number): NavigationItem => {
          const umbrellaSlug = label.slug?.toLowerCase() || slugify(label.name);
          const url = label.id
            ? `/listing/${umbrellaSlug}?category_id=${label.id}`
            : `/listing/${umbrellaSlug}`;

          return {
            id: `label-${label.id || index}`,
            title: label.name,
            slug: umbrellaSlug,
            url: scrubUrl(url),
            subTypes: mapItems(
              (label.collections || []) as RawNavigationItem[],
              umbrellaSlug,
            ),
            content: label.content || "",
          };
        },
      );
      navigation.push(...labels);
    }

    // 2. Process nav_bar_collections (Individual top-level collections)
    if (nav_bar_collections && nav_bar_collections.length > 0) {
      const collectionLabels = nav_bar_collections.map(
        (item: RawNavigationItem): NavigationItem => {
          const umbrellaSlug =
            item.slug?.toLowerCase() || slugify(item.name || "");
          return {
            id: item.id,
            title: (item.name || item.label || item.title) as string,
            slug: umbrellaSlug,
            url: scrubUrl(`/listing/${umbrellaSlug}?id=${item.id}`),
            subTypes: mapItems(
              (item.children || []) as RawNavigationItem[],
              umbrellaSlug,
            ),
            content: item.content || "",
          };
        },
      );
      navigation.push(...collectionLabels);
    }

    return navigation;
  } catch (error) {
    console.error("Error fetching navigation links:", error);
    return [];
  }
};

export { getNavigationLinks };
